import {
    Alert,
    Button,
    Center,
    Container,
    Loader,
    Paper,
    Stack,
    Text,
    Title
} from "@mantine/core";
import { CheckCircle, Email, Error, Home } from "@mui/icons-material";
import { useUnsubscribeFromLinkMutation } from "@therapprove/common";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { unsubscribeEmailBase64ToPayload } from "../utils/payload.utils";

export const UnsubscribeEmail = () => {
    // Get the encoded unsubscribe key parameter from the URL path
    const { encodedUnsubscribeKeys } = useParams<{
        encodedUnsubscribeKeys: string;
    }>();

    // State variables
    const [pageState, setPageState] = useState<
        "error" | "success" | "loading" | null
    >(null);

    // Mutations
    const [, unsubscribeFromLink] = useUnsubscribeFromLinkMutation();

    // Decode the unsubscribe key
    const decodedKeys = useMemo(() => {
        if (!encodedUnsubscribeKeys) return undefined;

        try {
            const payload = unsubscribeEmailBase64ToPayload(
                encodedUnsubscribeKeys
            );
            if (!payload) {
                setPageState("error");
                return undefined;
            }

            return payload;
        } catch (e) {
            setPageState("error");
            console.error(e);
            return undefined;
        }
    }, [encodedUnsubscribeKeys]);

    // Execute mutation when decoded keys are available
    useEffect(() => {
        if (decodedKeys?.key1 && decodedKeys?.key2) {
            setPageState("loading");

            unsubscribeFromLink({
                key1: decodedKeys.key1,
                key2: decodedKeys.key2
            })
                .then(({ data, error }) => {
                    const success =
                        data?.emailPreferences?.unsubscribeFromLink?.success;
                    if (success) {
                        setPageState("success");
                    } else if (!success || error) {
                        setPageState("error");
                    }
                })
                .catch(() => {
                    setPageState("error");
                });
        }
    }, [decodedKeys, unsubscribeFromLink]);

    if (pageState === "error") {
        return (
            <Container size="sm" py="xl">
                <Paper shadow="sm" p="xl" radius="md">
                    <Stack gap="lg" align="center">
                        <Alert
                            icon={<Error color="error" />}
                            title="Unsubscribe Failed"
                            color="var(--mantine-color-red-5)"
                            variant="light"
                            style={{ width: "100%" }}
                        >
                            <Text variant="body" size="sm" mb="md">
                                We encountered an error while processing your
                                unsubscribe request.
                            </Text>
                            <Text variant="body-muted">
                                Please contact support if you continue to
                                receive emails.
                            </Text>
                        </Alert>

                        <Button
                            onClick={() =>
                                (window.location.href =
                                    "mailto:support@therapprove.com")
                            }
                            leftSection={<Email />}
                        >
                            Contact Support
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    if (pageState === "success") {
        return (
            <Container size="sm" py="xl">
                <Paper shadow="sm" p="xl" radius="md">
                    <Stack gap="lg" align="center">
                        <Alert
                            icon={<CheckCircle color="success" />}
                            title="Successfully Unsubscribed"
                            color="primary"
                            variant="light"
                            style={{ width: "100%" }}
                        >
                            <Text variant="body" size="sm" mb="md">
                                You have been successfully unsubscribed from
                                this type of email.
                            </Text>
                            <Text variant="body-muted">
                                You will no longer receive emails of this type.
                                You can always resubscribe by selecting 'manage
                                preferences' in any email received from
                                Therapprove.
                            </Text>
                        </Alert>

                        <Button
                            onClick={() =>
                                (window.location.href =
                                    "https://therapprove.com")
                            }
                            leftSection={<Home />}
                        >
                            Visit Therapprove
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        );
    }

    return (
        <Container size="sm" py="xl">
            <Paper shadow="sm" p="xl" radius="md">
                <Center>
                    <Stack gap="lg" align="center">
                        <Loader size="lg" />
                        <Title order={3}>Processing Unsubscribe Request</Title>
                        <Text variant="body-muted" ta="center">
                            Please wait while we process your unsubscribe
                            request...
                        </Text>
                    </Stack>
                </Center>
            </Paper>
        </Container>
    );
};
